import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { THEME_PAGES, THEME_PAGE_SLUGS, type ThemePage } from "@/data/theme-pages";
import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";
import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";

export const Route = createFileRoute("/soirees-thematiques/$slug")({
  loader: ({ params }) => {
    const page = THEME_PAGES[params.slug];
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.page as ThemePage | undefined;
    if (!p) return { meta: [{ title: "Soirée thématique | DJ Val" }, { name: "robots", content: "noindex" }] };
    const url = `https://dj-val.fr/soirees-thematiques/${p.slug}`;
    return {
      meta: [
        { title: p.metaTitle },
        { name: "description", content: metaDesc(p.metaDescription) },
        { property: "og:title", content: p.metaTitle },
        { property: "og:description", content: metaDesc(p.metaDescription) },
        { property: "og:image", content: absoluteImageUrl(p.hero) },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: p.name,
            description: p.metaDescription,
            serviceType: p.name,
            provider: { ...LOCAL_BUSINESS_SCHEMA },
            areaServed: ["Cavaillon", "Avignon", "Vaucluse", "Bouches-du-Rhône", "Gard"],
            url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Soirée introuvable</h1>
        <Link to="/soirees-thematiques" className="mt-4 inline-block text-[var(--gold)] underline">
          Retour aux soirées thématiques
        </Link>
      </div>
    </div>
  ),
  component: ThemePageView,
});

function ThemePageView() {
  useReveal();
  const { page } = Route.useLoaderData();
  const p: ThemePage = page;

  return (
    <div className="bg-background text-muted-foreground">
      <section className="on-dark relative min-h-[75vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img loading="lazy" decoding="async" src={p.hero} alt={`${p.name} animée par DJ Val en Vaucluse`} className="h-full w-full object-cover opacity-40 kenburns-img" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">{p.eyebrow}</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">{p.h1}</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">{p.intro}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Link to="/contactez-nous" search={{ sujet: `Devis ${p.name}` }} className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all text-center">
              Devis gratuit
            </Link>
            <a href="tel:0767908667" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/90 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30">
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
      <ReviewsProof />

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {p.pillars.map((pil, i) => (
            <div key={pil.title} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="font-serif-display text-2xl text-foreground mt-4">{pil.title}</h2>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{pil.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-8 px-6 max-w-4xl mx-auto space-y-14">
        {p.sections.map((s) => (
          <div key={s.h2} className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl text-foreground">{s.h2}</h2>
            <span className="gold-rule !w-10 my-5" />
            <div className="space-y-4">
              {s.paragraphs.map((t) => (
                <p key={t.slice(0, 40)} className="text-sm text-muted-foreground leading-relaxed font-light">{t}</p>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">{p.name}</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {p.faq.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto text-center">
          <p className="eyebrow text-[var(--gold)]">À découvrir aussi</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/location-materiel/$slug" params={{ slug: p.equipment.slug }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              {p.equipment.label}
            </Link>
            {THEME_PAGE_SLUGS.filter((s) => s !== p.slug).map((s) => (
              <Link key={s} to="/soirees-thematiques/$slug" params={{ slug: s }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                {THEME_PAGES[s].name}
              </Link>
            ))}
            <Link to="/soirees-thematiques" className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Toutes les soirées thématiques
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
